import { SITE_URL } from 'api/constants';
import { getCasinoRTP } from 'utils/casino';
import { clearMarkdown } from 'utils/markdown';
import { getOpenGraphImage } from 'utils/query';
import { getItemIdentifier } from 'utils/slug';
import { Routes } from './routes';
import {
  IBigWins,
  IBlogPost,
  ICasino,
  ICasinoCollection,
  IComplaint,
  IProvider,
  IQuestion,
  ISlot,
  ISlotCollection,
  IStreamer,
  IStreamerCollection,
} from './types';
import { TeamMember } from 'components/template/about-us/teamData';

const defaultParams = {
  locale: 'en_US',
  type: 'website',
  siteName: 'RTPCheck',
  image: getOpenGraphImage({}),
};

export const allMetadata = {
  home: {
    ...defaultParams,
    title: `RTPCheck.com - RTP Inspection in Online Casino`,
    description: `Discover the top-rated online casinos where you can play and win real money. Our carefully curated list highlights the best platforms known for their exceptional game selection, lucrative bonuses, secure payment options, and outstanding player experiences. Whether you're a seasoned player or a newcomer, find the perfect casino to suit your needs and start enjoying thrilling games while aiming for big wins!`,
    url: `${SITE_URL}${Routes.home}`,
    canonicalUrl: `${SITE_URL}${Routes.home}`,
  },
  login: {
    ...defaultParams,
    title: `Login 🍒 RTPCheck`,
    canonicalUrl: `${SITE_URL}${Routes.login}`,
    url: `${SITE_URL}${Routes.login}`,
  },
  blog: {
    ...defaultParams,
    title: `Useful Guides for Real Gamblers`,
    description: `Discover the ultimate guides for gamblers in online casinos. Read now and win big!`,
    canonicalUrl: `${SITE_URL}${Routes.blogAndNews}`,
    url: `${SITE_URL}${Routes.blogAndNews}`,
  },
  blogAndNews: {
    ...defaultParams,
    title: `Top Topics Online Casinos: Essential Guides and Insights`,
    description: `Explore a curated selection of key topics about online casinos. From detailed game guides to bonus strategies and security tips, we cover everything you need to enhance your online gaming experience.`,
    canonicalUrl: `${SITE_URL}${Routes.blogAndNews}`,
    url: `${SITE_URL}${Routes.blogAndNews}`,
  },
  blogAndNewsNews: {
    ...defaultParams,
    title: `News from the World of Online Casinos and Other Gambling Games`,
    description: `The world of online gambling is constantly evolving, with new casinos opening, games launching, and regulations shifting. Staying informed with the latest news in the gambling industry is essential for players who want to keep up with these changes. Whether it’s about the latest slot game release, updates on gambling laws, or major industry events, the news section provides up-to-date information that can impact your gaming experience. You’ll learn about new innovations, notable wins, emerging trends, and significant changes in casino operations. Keeping track of the news allows players to make informed decisions, find the best casinos, and understand what’s going on in the world of gambling.`,
    canonicalUrl: `${SITE_URL}${Routes.blogAndNews}`,
    url: `${SITE_URL}${Routes.blogAndNews}`,
  },
  blogAndNewsBlogs: {
    ...defaultParams,
    title: `Online Casino Players' Blog`,
    description: `An online casino players' blog is a valuable resource for both novice and seasoned gamblers. These blogs offer firsthand experiences, insightful tips, and strategies from people who actively play at online casinos. Bloggers often discuss their wins, losses, and everything in between, providing honest reviews and helping others navigate the complex world of online gambling. Topics may include the best games, the most reputable casinos, bonus opportunities, and gameplay strategies. Additionally, player blogs often feature discussions on recent casino trends, player feedback, and personal experiences with new features or updates. For those looking to learn more or refine their gaming techniques, a well-maintained blog can be an excellent source of knowledge and community connection.`,
    canonicalUrl: `${SITE_URL}${Routes.blogAndNews}`,
    url: `${SITE_URL}${Routes.blogAndNews}`,
  },
  blogAndNewsRTPChecks: {
    ...defaultParams,
    title: `Results of RTP Checks at Online Casinos`,
    description: `Return to Player (RTP) percentages are a critical factor in determining the fairness of online casino games. RTP refers to the percentage of total wagers that a game will return to players over time. However, not all casinos and games are created equal—some may not be as transparent about their RTP rates, and players may find themselves at a disadvantage. Our RTP checks thoroughly investigate the fairness of games at popular casino brands. We expose any discrepancies, ensuring that players are aware of casinos offering fair chances and those that may be attempting to scam players. Our checks aim to protect players and guide them toward reliable casinos with honest RTP rates.`,
    canonicalUrl: `${SITE_URL}${Routes.blogAndNews}`,
    url: `${SITE_URL}${Routes.blogAndNews}`,
  },
  blogAndNewsBigwins: {
    ...defaultParams,
    title: `Big Wins at Online Casinos of Players and Streamers`,
    description: `Big wins at online casinos not only create buzz but also inspire players worldwide. From hitting massive jackpots on slots to securing monumental wins at table games, these stories are celebrated across the gaming community. Players and streamers often share their big win stories, whether it's through streaming platforms or social media. These moments are not just about the money—they highlight skill, strategy, and a bit of luck. These wins can motivate new players to try their luck and help them understand the importance of playing the right games and using effective strategies. Whether it’s a jackpot or a huge streak of good luck, big wins always make for exciting and memorable stories.`,
    canonicalUrl: `${SITE_URL}${Routes.blogAndNews}`,
    url: `${SITE_URL}${Routes.blogAndNews}`,
  },
  guide: {
    ...defaultParams,
    title: `Useful Guides for Real Gamblers`,
    description: `Discover the ultimate guides for gamblers in online casinos. Read now and win big!`,
    canonicalUrl: `${SITE_URL}${Routes.guides}`,
    url: `${SITE_URL}${Routes.guides}`,
  },
  guides: {
    ...defaultParams,
    title: `Beginner's Guide – The Best Online Casino Guides`,
    description: `Explore our comprehensive guide to online gambling. Whether you're a seasoned player or new to the scene, we've gathered all the essential information you need to keep your money safe and enjoy a successful gaming experience.`,
    canonicalUrl: `${SITE_URL}${Routes.guides}`,
    url: `${SITE_URL}${Routes.guides}`,
  },
  news: {
    ...defaultParams,
    title: `Latest News for Real Gamblers`,
    description: `Discover the latest news for gamblers in online casinos. Read now and win big!`,
    canonicalUrl: `${SITE_URL}${Routes.news}`,
    url: `${SITE_URL}${Routes.news}`,
  },
  topic: {
    ...defaultParams,
    title: `Latest Topics for Real Gamblers`,
    description: `Discover the latest topics for gamblers in online casinos. Read now and win big!`,
    canonicalUrl: `${SITE_URL}${Routes.blogAndNews}`,
    url: `${SITE_URL}${Routes.blogAndNews}`,
  },
  rtpguide: {
    ...defaultParams,
    title: `RTP Guides 🍒 RTPCheck`,
    description: `Discover the ultimate guides for gamblers in online casinos. Read now and win big!`,
    canonicalUrl: `${SITE_URL}${Routes.guidesRtpChecks}`,
    url: `${SITE_URL}${Routes.guidesRtpChecks}`,
  },
  blogPost: (post: IBlogPost) => ({
    ...defaultParams,
    title: `${clearMarkdown(`${post?.metaTitle ?? post.title}`)} 🍒 RTPCheck`,
    description: post?.metaDescription,
    canonicalUrl: `${SITE_URL}${Routes.dynamicBlogPage(post)}`,
    url: `${SITE_URL}${Routes.dynamicBlogPage(post)}`,
    image: getOpenGraphImage({
      type: 'post',
      name: post?.title,
      image: post?.imageUrl,
    }),
  }),
  myProfile: {
    ...defaultParams,
    title: `My profile 🍒 RTPCheck`,
    canonicalUrl: `${SITE_URL}${Routes.profile}`,
    url: `${SITE_URL}${Routes.profile}`,
  },
  publicProfile: (username?: string) => ({
    ...defaultParams,
    title: `${username} 🍒 RTPCheck`,
    canonicalUrl: `${SITE_URL}${Routes.publicProfile(username ?? '')}`,
    url: `${SITE_URL}${Routes.publicProfile(username ?? '')}`,
  }),
  allCasinos: {
    ...defaultParams,
    title: `Casino rating - The best online casinos for real money.`,
    description: `Discover the top-rated online casinos where you can play and win real money. Our carefully curated list highlights the best platforms known for their exceptional game selection, lucrative bonuses, secure payment options, and outstanding player experiences. Whether you're a seasoned player or a newcomer, find the perfect casino to suit your needs and start enjoying thrilling games while aiming for big wins!`,
    canonicalUrl: `${SITE_URL}${Routes.allCasinos}`,
    url: `${SITE_URL}${Routes.allCasinos}`,
  },
  allCasinosCollection: {
    ...defaultParams,
    title: 'Casino Collections 🍒 RTPCheck',
    description: 'Online Casino Collections by RTPCheck',
  },
  casinoCollection: (collection: ICasinoCollection | ISlotCollection) => ({
    ...defaultParams,
    title: `${collection?.name} 🍒 RTPCheck`,
    description: `${collection?.description} by RTPCheck`,
  }),
  'crypto-casinos': {
    ...defaultParams,
    title: `Best Crypto and Bitcoin Casinos with Real RTP`,
    description: `Explore the best crypto and Bitcoin casinos offering real Return to Player (RTP) rates. These casinos provide a secure and transparent gaming.`,
  },
  'trusted-casinos': {
    ...defaultParams,
    title: `Trusted Online Casinos with Maximum RTP`,
    description: `Casinos offering games with a high Return to Player (RTP) percentage provide better odds and enhance the overall gaming experience.`,
  },
  'new-casinos': {
    ...defaultParams,
    title: `Trusted Online Casinos with Maximum RTP`,
    description: `Explore the latest online casinos that prioritize fairness, transparency, and high Return to Player (RTP) rates. On this page, you’ll find newly launched platforms that have been tested for honest gameplay, innovative features, and reliable payouts. Whether you’re searching for fresh bonus offers, modern game libraries, or improved mobile compatibility, our curated list highlights the newest online casinos where you can play with real RTP data. Stay ahead of the curve and discover where the next big win might be waiting.`,
  },
  'blacklisted-casinos': {
    ...defaultParams,
    title: `Blacklist of online casinos - list of scammers.`,
    description: `This section contains a regularly updated blacklist of online casinos that have been caught scamming players, refusing to pay winnings, using fake licenses, or manipulating game outcomes. These are casinos that we strongly advise you to avoid at all costs. Every platform listed here has been investigated by the RTPChecking.com team and has shown patterns of dishonest behavior or unresolved complaints from multiple users. If you value your money and fair gameplay, do not register or deposit on any of these sites.`,
  },
  casinoPage: (casino: ICasino) => ({
    ...defaultParams,
    title:
      casino?.metaTitle ??
      `${casino?.name} Review 🍒 Unbiased Analysis, Games, and Bonuses`,
    description:
      casino?.metaDescription ??
      `Discover the ultimate ${casino?.description} review, featuring an unbiased analysis of games, bonuses, and more. Get the inside scoop on this top-rated online casino.`,
    canonicalUrl: `${SITE_URL}${Routes.casinoPage(getItemIdentifier(casino))}`,
    url: `${SITE_URL}${Routes.casinoPage(getItemIdentifier(casino))}`,
    image: getOpenGraphImage({
      type: 'casino',
      name: casino?.name,
      logo: casino?.logoUrl,
      rating: getCasinoRTP(casino).number?.toString(),
      image: casino?.imageUrl,
    }),
  }),
  casinoReviews: {
    ...defaultParams,
    title: `Players Reviews - Unbiased Feedback from Real Gamblers`,
    description: [
      `Discover what players are saying about`,
      `Read unbiased reviews from real gamblers and make an informed decision. Join the excitement today!`,
    ],
  },
  casinoSlotReviews: (casino: ICasino) => ({
    ...defaultParams,
    title: `Discover High/Low/Medium RTP ${casino?.name} Slots | Best/Worst/Average Payouts`,
    description: `Explore the exciting world of ${casino?.description} Slots with high/low/medium RTP rates. Play now and win big!`,
    canonicalUrl: `${SITE_URL}${Routes.casinoSlotReviewsPage(
      getItemIdentifier(casino)
    )}`,
    url: `${SITE_URL}${Routes.casinoSlotReviewsPage(getItemIdentifier(casino))}`,
    image: getOpenGraphImage({
      type: 'casino',
      name: casino?.name,
      logo: casino?.logoUrl,
      rating: getCasinoRTP(casino).number?.toString(),
      image: casino?.imageUrl,
    }),
  }),
  casinoBigWins: (casino: ICasino) => ({
    ...defaultParams,
    title: `Discover Big Wins ${casino?.name} Slots`,
    description: `Explore the big wins of ${casino?.description} Slots. Play now and win big!`,
    image: getOpenGraphImage({
      name: casino?.name,
      type: 'bigwins casino',
      image: casino?.imageUrl,
    }),
    canonicalUrl: `${SITE_URL}${Routes.casinoBigWinsPage(
      getItemIdentifier(casino)
    )}`,
    url: `${SITE_URL}${Routes.casinoBigWinsPage(getItemIdentifier(casino))}`,
  }),
  allSlots: {
    ...defaultParams,
    title: `Possible RTP of Online Slots and Casino Gamess`,
    description: `Description: Understanding the RTP (Return to Player) of online slots and casino games is key to making informed betting decisions. This guide will help you explore how RTP affects your chances of winning, how to find it, and why it matters when playing at online casinos.`,
    canonicalUrl: `${SITE_URL}${Routes.allSlots}`,
    url: `${SITE_URL}${Routes.allSlots}`,
  },
  allSlotsCollections: {
    ...defaultParams,
    title: `Slots Collections 🍒 RTPCheck`,
    description: `Online Slots Collections by RTPCheck`,
  },
  slotsCollection: (collection: ISlotCollection) => ({
    ...defaultParams,
    title: `🍒 RTPCheck ${collection.name}`,
    description: `${collection.description} by RTPCheck`,
    canonicalUrl: `${SITE_URL}${Routes.slotCollections(
      getItemIdentifier(collection)
    )}`,
    url: `${SITE_URL}${Routes.slotCollections(getItemIdentifier(collection))}`,
  }),
  slot: (slot: ISlot) => ({
    ...defaultParams,
    title:
      slot?.metaTitle ??
      `Slot ${slot?.name} Possible RTP, Free Demo online, Big wins.`,
    description:
      slot?.metaDescription ??
      `Read full information about Slot ${slot?.name}, check the possible RTP, play demo of the Slot online and for free, check out the big wins in videos and screenshots.`,

    canonicalUrl: `${SITE_URL}${Routes.slotPage(getItemIdentifier(slot))}`,
    url: `${SITE_URL}${Routes.slotPage(getItemIdentifier(slot))}`,
    image: getOpenGraphImage({
      name: slot?.name,
      type: 'slot',
      image: slot?.imageUrl,
    }),
  }),
  slotReviews: (slot: ISlot) => ({
    ...defaultParams,
    title: `${slot?.name}Slot RTP - Discover the Return to Player Percentage at All Casinos`,
    description: `Find out the RTP percentage of the ${slot?.description} Slot at various casinos. Compare and choose the best odds for maximum winnings. Start playing now!`,
    image: getOpenGraphImage({
      name: slot?.name,
      type: 'slot',
      image: slot?.imageUrl,
    }),
  }),
  slotsForRealMoney: {
    ...defaultParams,
    title: [`Play`, `Slot for Real Money - Exciting Casino Game 🍒 RTPCheck`],
    description: [
      `Experience the thrill of playing`,
      `Slot for real money at online casino. Win big with this exciting and immersive game. Join now and start winning!`,
    ],
  },
  bigwinsSlots: (slot: ISlot) => ({
    ...defaultParams,
    title: `${slot?.name} Slot Big Wins Collection 🍒 RTPCheck`,
    image: getOpenGraphImage({
      name: slot?.name,
      type: 'bigwins slots',
      image: slot?.imageUrl,
    }),
    canonicalUrl: `${SITE_URL}${Routes.slotBigWinsPage(
      getItemIdentifier(slot)
    )}`,
    url: `${SITE_URL}${Routes.slotBigWinsPage(getItemIdentifier(slot))}`,
  }),
  bigwin: (slot?: ISlot, bigwin?: IBigWins) => ({
    ...defaultParams,
    title: `${slot?.name} Slot Big Win ${bigwin?.name}`,
    description: bigwin?.description,
    canonicalUrl: `${SITE_URL}${Routes.bigwin(getItemIdentifier(bigwin))}`,
    url: `${SITE_URL}${Routes.bigwin(getItemIdentifier(bigwin))}`,
    image: getOpenGraphImage({
      name: bigwin?.name,
      type: 'bigwin',
      image: bigwin?.imageUrl,
    }),
  }),
  allBigwins: {
    ...defaultParams,
    title: `Slot Big Wins 🍒 RTPCheck`,
    canonicalUrl: `${SITE_URL}${Routes.bigwins}`,
    url: `${SITE_URL}${Routes.bigwins}`,
  },
  aboutUs: {
    ...defaultParams,
    title: `About Us - Contacts - RTPCheck.com Team`,
    description:
      'At RTPCheck.com, we are a dedicated team of iGaming experts committed to providing accurate and up-to-date RTP (Return to Player) information for online casino games. Our goal is to help players make informed decisions by analyzing game fairness, payout rates, and casino reliability. For inquiries, partnerships, or support, feel free to contact us. Our team is always ready to assist you!',
    canonicalUrl: `${SITE_URL}${Routes.aboutUs}`,
    url: `${SITE_URL}${Routes.aboutUs}`,
  },
  aboutUsMember: (member: TeamMember) => ({
    ...defaultParams,
    title: `${member.name} - ${member.position} 🍒 RTPCheck.com Team`,
    description:
      member.description ||
      'At RTPCheck.com, we are a dedicated team of iGaming experts committed to providing accurate and up-to-date RTP (Return to Player) information for online casino games. Our goal is to help players make informed decisions by analyzing game fairness, payout rates, and casino reliability. For inquiries, partnerships, or support, feel free to contact us. Our team is always ready to assist you!',
    canonicalUrl: `${SITE_URL}${Routes.aboutThisAuthor(member.name)}`,
    url: `${SITE_URL}${Routes.aboutThisAuthor(member.name)}`,
  }),
  privacy: {
    ...defaultParams,
    title: `Privacy Policy 🍒 RTPCheck`,
    canonicalUrl: `${SITE_URL}${Routes.privacy}`,
    url: `${SITE_URL}${Routes.privacy}`,
  },
  terms: {
    ...defaultParams,
    title: `Terms And Conditions 🍒 RTPCheck`,
    canonicalUrl: `${SITE_URL}${Routes.terms}`,
    url: `${SITE_URL}${Routes.terms}`,
  },
  cookie: {
    ...defaultParams,
    title: `Cookie Policy 🍒 RTPCheck`,
    canonicalUrl: `${SITE_URL}${Routes.cookie}`,
    url: `${SITE_URL}${Routes.cookie}`,
  },
  responsibleGaming: {
    ...defaultParams,
    title: `Responsible Gaming 🍒 RTPCheck`,
    canonicalUrl: `${SITE_URL}${Routes.responsibleGaming}`,
    url: `${SITE_URL}${Routes.responsibleGaming}`,
  },
  providers: {
    ...defaultParams,
    title: `The Best Online Casino Game Providers`,
    description:
      'Discover the leading game providers in the online casino industry. From innovative slot games to immersive live dealer experiences, these top-rated companies have set the standard for online gambling. Learn about their key features and what makes them stand out',
    canonicalUrl: `${SITE_URL}${Routes.providers}`,
    url: `${SITE_URL}${Routes.providers}`,
  },
  provider: (provider: IProvider) => ({
    ...defaultParams,
    title: `${provider?.name} 🍒 RTPCheck`,
    description: `${provider?.name} by RTPCheck`,
    canonicalUrl: `${SITE_URL}${Routes.provider(getItemIdentifier(provider))}`,
    url: `${SITE_URL}${Routes.provider(getItemIdentifier(provider))}`,
  }),
  allComplaints: {
    ...defaultParams,
    title: `How to Handle Complaints About Online Casinos – A Player’s Guide`,
    description:
      'Online casinos and gambling sites can sometimes leave players frustrated due to issues like delayed withdrawals, unfair game outcomes, or unresponsive customer support. This guide provides essential tips on how to file complaints, resolve disputes, and find reliable assistance when facing problems with an online casino. Learn how to protect yourself, recognize red flags, and ensure a fair gaming experience.',
    canonicalUrl: `${SITE_URL}${Routes.complaints}`,
    url: `${SITE_URL}${Routes.complaints}`,
  },
  complaint: (complaint: IComplaint) => ({
    ...defaultParams,
    title: `${complaint.title} 🍒 RTPCheck`,
    description: `Complaint ${complaint.title} by RTP`,
    canonicalUrl: `${SITE_URL}${Routes.complaint(
      getItemIdentifier(complaint)
    )}`,
    url: `${SITE_URL}${Routes.complaint(getItemIdentifier(complaint))}`,
  }),
  allQuestions: {
    ...defaultParams,
    title: `We Answer Any Questions About Gambling`,
    description:
      'Have any questions about gambling or online casinos? Our platform allows you to get the answers you need directly from experts or fellow players. Get insights into games, strategies, security, and more!',
    canonicalUrl: `${SITE_URL}${Routes.questions}`,
    url: `${SITE_URL}${Routes.questions}`,
  },
  question: (question: IQuestion) => ({
    ...defaultParams,
    title: `${question.title} 🍒 RTPCheck`,
    description: `Question ${question.title} by RTP`,
    canonicalUrl: `${SITE_URL}${Routes.question(getItemIdentifier(question))}`,
    url: `${SITE_URL}${Routes.question(getItemIdentifier(question))}`,
  }),
  streamerCollection: (collection: IStreamerCollection) => ({
    ...defaultParams,
    title: `${collection?.name} 🍒 Streamers RTPCheck`,
    description: `${collection?.description} Streamers by RTPCheck`,
  }),
  streamer: (streamer: IStreamer) => ({
    ...defaultParams,
    title: `${streamer?.metaTitle ?? `${streamer.username} 🍒 RTPCheck`}`,
    description: `${
      streamer?.metaDescription ?? `Streamer ${streamer.username} by RTP`
    }`,
    canonicalUrl: `${SITE_URL}${Routes.streamer(getItemIdentifier(streamer))}`,
    url: `${SITE_URL}${Routes.streamer(getItemIdentifier(streamer))}`,
  }),
  streamers: {
    ...defaultParams,
    title: `The Best Online Casino Streamers! Ratings, Reviews, and Insights`,
    description: `Explore the world of online casino streamers! Discover ratings, reviews, and the hidden truths about gambling streams, including fake money scams and deceptive practices. Stay informed and make better choices.`,
    canonicalUrl: `${SITE_URL}${Routes.streamers}`,
    url: `${SITE_URL}${Routes.streamers}`,
  },
};
